import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { RootState } from '../../redux/store';
import { removeFromBuylist, clearBuylist } from '../../redux/buylistSlice';
import { getBuylistImageUrl } from '../../services/buylistService';
import { theme } from '../../styles/theme';
import { FoilBadge } from '../../styles/components';

// ========== Sidebar variant styles ==========

const SidebarContainer = styled.aside`
  width: 260px;
  flex-shrink: 0;
`;

const SidebarCard = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.card};
  overflow: hidden;
  position: sticky;
  top: calc(var(--tp-header-height) + ${theme.spacing.md});
`;

const SidebarHeader = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: ${theme.colors.success};
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SidebarTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const CountBadge = styled.span`
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: ${theme.borderRadius.full};
  padding: 2px 8px;
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.bold};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.white};
  font-size: ${theme.fontSizes.lg};
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    opacity: 0.8;
  }
`;

const SidebarBody = styled.div`
  padding: ${theme.spacing.sm};
`;

const flashHighlight = keyframes`
  0% { background-color: rgba(45, 184, 107, 0.3); }
  100% { background-color: transparent; }
`;

const CartItemRow = styled.div<{ isNew?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} 0;
  border-bottom: 1px solid ${theme.colors.gray.light};
  animation: ${props => props.isNew ? flashHighlight : 'none'} 1s ease-out;

  &:last-child {
    border-bottom: none;
  }
`;

const ItemThumbnail = styled.div<{ $imageUrl: string }>`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  background-image: url(${props => props.$imageUrl});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${theme.colors.gray.lightest};
  border-radius: ${theme.borderRadius.sm};
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.gray.dark};
`;

const ItemPriceQty = styled.p`
  margin: 2px 0 0 0;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.success};
  font-weight: ${theme.fontWeights.medium};
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.gray.medium};
  font-size: ${theme.fontSizes.md};
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    color: ${theme.colors.danger};
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.gray.light};
  margin: ${theme.spacing.xs} 0;
`;

const CartSummary = styled.div`
  padding: ${theme.spacing.xs} 0;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${theme.fontSizes.sm};
  padding: 3px 0;
  color: ${theme.colors.text};
`;

const SummaryValue = styled.span<{ highlight?: boolean }>`
  font-weight: ${theme.fontWeights.semibold};
  color: ${props => props.highlight ? theme.colors.success : theme.colors.text};
`;

const CartActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.xs};
`;

const ViewCartButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.xs};
  background-color: ${theme.colors.success};
  color: ${theme.colors.white};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  cursor: pointer;
  transition: opacity ${theme.transitions.fast};

  &:hover {
    opacity: 0.9;
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.danger};
  font-size: ${theme.fontSizes.xs};
  cursor: pointer;
  text-align: center;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.md} ${theme.spacing.sm};
  color: ${theme.colors.gray.dark};
  font-size: ${theme.fontSizes.sm};
`;

// ========== Drawer variant styles ==========

const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const DrawerPanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 320px;
  background-color: ${theme.colors.white};
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 400px) {
    width: 100vw;
  }
`;

const DrawerHeader = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: ${theme.colors.success};
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing.sm};
`;


const FloatingButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 100;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${theme.colors.success};
  color: ${theme.colors.white};
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  transition: transform ${theme.transitions.fast};

  &:hover {
    transform: scale(1.1);
  }

  @media (min-width: 1200px) {
    display: none;
  }
`;

const FloatingBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  background-color: ${theme.colors.danger};
  color: ${theme.colors.white};
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.7rem;
  font-weight: ${theme.fontWeights.bold};
`;

// ========== Shared cart content ==========

interface CartContentProps {
  showCloseButton?: boolean;
  onClose?: () => void;
  maxListHeight?: string;
}

const CartContent: React.FC<CartContentProps> = ({ showCloseButton, onClose, maxListHeight }) => {
  const { items, totalQuantity, totalAmount } = useSelector((state: RootState) => state.buylist);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [lastAddedId, setLastAddedId] = useState<number | null>(null);

  // Track newly added items for highlight animation
  useEffect(() => {
    if (items.length > 0) {
      const latestItem = items[items.length - 1];
      setLastAddedId(latestItem.item.buylistID);
      const timer = setTimeout(() => setLastAddedId(null), 1000);
      return () => clearTimeout(timer);
    }
  }, [items.length]);

  if (items.length === 0) {
    return <EmptyState>Salgskurven er tom</EmptyState>;
  }

  return (
    <>
      <div style={{ maxHeight: maxListHeight || 'calc(100vh - 340px)', overflowY: 'auto' }}>
        {items.map(({ item, quantity }) => (
          <CartItemRow
            key={item.buylistID}
            isNew={item.buylistID === lastAddedId}
          >
            <ItemThumbnail $imageUrl={getBuylistImageUrl(item.cardBlueprintId, item.image_url)} />
            <ItemInfo>
              <ItemName title={item.blueprintName}>{item.blueprintName}</ItemName>
              <ItemMeta>{item.expansionName}</ItemMeta>
              {item.foil && <FoilBadge>✨ Foil</FoilBadge>}
              <ItemPriceQty>
                {quantity} stk · {((item.buyPrice || 0) * quantity).toFixed(2)} kr
              </ItemPriceQty>
            </ItemInfo>
            <RemoveButton
              onClick={() => dispatch(removeFromBuylist(item.buylistID))}
              title="Fjern"
            >
              ×
            </RemoveButton>
          </CartItemRow>
        ))}
      </div>

      <Divider />

      <CartSummary>
        <SummaryRow>
          <span>Antall:</span>
          <SummaryValue>{totalQuantity}</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <span>Kontant:</span>
          <SummaryValue>{totalAmount.toFixed(2)} kr</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <span>Kreditt (+10%):</span>
          <SummaryValue highlight>{(totalAmount * 1.1).toFixed(2)} kr</SummaryValue>
        </SummaryRow>
      </CartSummary>

      <CartActions>
        <ViewCartButton onClick={() => navigate('/buylist-cart')}>
          Se full salgskurv
        </ViewCartButton>
        <ClearButton onClick={() => dispatch(clearBuylist())}>
          Tøm kurv
        </ClearButton>
      </CartActions>
    </>
  );
};

// ========== Main component ==========

interface BuylistCartSidebarProps {
  variant: 'sidebar' | 'drawer';
  onClose?: () => void;
}

const BuylistCartSidebar: React.FC<BuylistCartSidebarProps> = ({ variant }) => {
  const { totalQuantity } = useSelector((state: RootState) => state.buylist);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Body scroll lock when drawer is open
  useEffect(() => {
    if (variant === 'drawer' && drawerOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [variant, drawerOpen]);

  if (variant === 'sidebar') {
    return (
      <SidebarContainer>
        <SidebarCard>
          <SidebarHeader>
            <SidebarTitle>
              Salgskurv
              {totalQuantity > 0 && <CountBadge>{totalQuantity}</CountBadge>}
            </SidebarTitle>
          </SidebarHeader>
          <SidebarBody>
            <CartContent />
          </SidebarBody>
        </SidebarCard>
      </SidebarContainer>
    );
  }

  // Drawer variant
  return (
    <>
      {totalQuantity > 0 && (
        <FloatingButton onClick={() => setDrawerOpen(true)}>
          <span style={{ transform: 'scaleX(-1)', display: 'inline-block' }}>🛒</span>
          <FloatingBadge>{totalQuantity}</FloatingBadge>
        </FloatingButton>
      )}

      {drawerOpen && (
        <DrawerOverlay onClick={() => setDrawerOpen(false)}>
          <DrawerPanel onClick={(e) => e.stopPropagation()}>
            <DrawerHeader>
              <SidebarTitle>
                Salgskurv
                {totalQuantity > 0 && <CountBadge>{totalQuantity}</CountBadge>}
              </SidebarTitle>
              <CloseButton onClick={() => setDrawerOpen(false)}>✕</CloseButton>
            </DrawerHeader>
            <DrawerBody>
              <CartContent maxListHeight="none" />
            </DrawerBody>
          </DrawerPanel>
        </DrawerOverlay>
      )}
    </>
  );
};

export default BuylistCartSidebar;
